import memberhubConstants from '~/constants/memberhub.constants'

/*
 * Session expiry and cross-tab session sync (CLUB-945).
 *
 * `$expireSession()` is what ApiService calls once the refresh endpoint has definitively
 * rejected the refresh token. It used to be a DOM `logout` event that only the dashboard
 * header components listened for, so in the funnel, contentful and custom layouts — and
 * on non-dashboard routes in the app — an expired session just kept failing. A plugin is
 * loaded everywhere, needs no component mounted, and can reach the store, router and toast.
 *
 * It does not call userStore.logout(): that hits DELETE /logout, revokes at Descope and
 * says "Successfully logged out", none of which applies to a session the server has
 * already rejected. clearAuthUser() is the primitive; the expiry toast is the only message.
 */
const RETURN_PATH_KEY = 'session_expired_return_to'
const GUARD_MS = 2000
const EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.'

type ReturnPath = { path: string, query: Record<string, unknown> }

/*
 * Routes under /dashboard that are themselves part of signing in. Coming back to one of
 * these after re-login would be wrong, so they are never recorded as a return path.
 */
const authRoutes: string[] = memberhubConstants.AUTH_ROUTES || []
const isProtectedPath = (path: string) =>
  path.startsWith('/dashboard') &&
  !authRoutes.includes(path.replace(/\/?$/, '/')) &&
  !path.startsWith('/dashboard/authorization/') &&
  !path.startsWith('/dashboard/setup-mfa') &&
  !path.startsWith('/dashboard/guest/')

const readReturnPath = (): ReturnPath | null => {
  try {
    const raw = sessionStorage.getItem(RETURN_PATH_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return typeof parsed?.path === 'string' && isProtectedPath(parsed.path) ? parsed : null
  } catch {
    return null
  }
}

const writeReturnPath = (value: ReturnPath | null) => {
  try {
    if (value) {
      sessionStorage.setItem(RETURN_PATH_KEY, JSON.stringify(value))
    } else {
      sessionStorage.removeItem(RETURN_PATH_KEY)
    }
  } catch {
    // sessionStorage can be unavailable (private mode, storage disabled); the return
    // path is a convenience, not a requirement.
  }
}

const parseStored = (key: string) => {
  try {
    const raw = getLocal(key)
    return raw && raw !== 'undefined' ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export default defineNuxtPlugin(nuxtApp => {
  let inFlight: Promise<void> | null = null

  // The router the app installed; read through nuxtApp so this plugin has no import of
  // its own to be mocked, and so it works before any page has rendered.
  const currentPath = (): string => (nuxtApp.$router as { currentRoute?: { value?: { path?: string } } })?.currentRoute?.value?.path || '/'
  const currentQuery = (): Record<string, unknown> =>
    ({ ...((nuxtApp.$router as { currentRoute?: { value?: { query?: Record<string, unknown> } } })?.currentRoute?.value?.query || {}) })

  /*
   * The login forms read `authModuleParams.source_page_url` for where to go after a
   * successful sign-in (see getAuthUserSourcePage), so a recorded return path is handed to
   * them that way. It is also kept in sessionStorage so a reload of the login page does
   * not lose it; the store is re-seeded from there below.
   */
  const seedReturnPath = (store: ReturnType<typeof useUserStore>, returnTo: ReturnPath | null) => {
    if (!returnTo) return
    store.authModuleParams = { source_page_url: returnTo.path, query: returnTo.query }
  }

  const expireSession = ({ reason }: { reason?: string } = {}) => {
    if (inFlight) return inFlight

    inFlight = (async () => {
      const store = useUserStore()
      const path = currentPath()
      const returnTo: ReturnPath | null = isProtectedPath(path)
        ? { path, query: currentQuery() }
        : null

      store.clearAuthUser()
      writeReturnPath(returnTo)
      seedReturnPath(store, returnTo)

      nuxtApp.$toast?.warning?.(EXPIRED_MESSAGE)

      // eslint-disable-next-line no-console
      if (reason) console.info('[session-expiry]', reason)

      await navigateTo(generateLoginPageUrl(), { replace: true })
    })().finally(() => {
      // Keep the guard up briefly after navigation: the 401s that queued behind the
      // shared refresh resolve after it, and each of them reaches this call.
      setTimeout(() => {
        inFlight = null
      }, GUARD_MS)
    })

    return inFlight
  }

  /*
   * A login page reload after expiry: put the return path back where the forms look.
   */
  const storedReturnPath = readReturnPath()
  if (storedReturnPath && !parseStored('token').access_token) {
    seedReturnPath(useUserStore(), storedReturnPath)
  }

  /*
   * Cross-tab sync. Another tab's logout (or expiry) removes `token` from localStorage;
   * this tab drops its in-memory copy and leaves protected routes, with no toast — the
   * tab that logged out already showed one. A login elsewhere sets `token`; this tab picks
   * it up so it stops looking signed out.
   */
  window.addEventListener('storage', event => {
    if (event.key !== 'token') return
    const store = useUserStore()

    if (event.newValue === null) {
      store.$patch({
        userProfile: {},
        token: {},
        descopeSession: {},
        pendingMfaChallenge: {},
        pendingMfaEnrollment: {},
        pendingMfaRecovery: {}
      })
      if (isProtectedPath(currentPath())) {
        navigateTo(generateLoginPageUrl(), { replace: true })
      }
      return
    }

    if (!store.token?.access_token) {
      store.$patch({
        token: parseStored('token'),
        userProfile: parseStored('auth_user')
      })
    }
  })

  return {
    provide: {
      expireSession
    }
  }
})
